import { GridSortModel } from "@mui/x-data-grid-pro";
import { Sort, SortDirection } from "../codegen/graphql";

// Asset list column key → `Sort.property` of searchAssetResults (IBM-1053).
// The backend accepts every scalar field of `SearchAssetResult.attributes` and
// `metrics`, one criterion per request. An unknown property does NOT fail — it
// silently falls back to `assetUrn` — so only properties verified against the
// gateway belong here; columns without an entry are not sortable. Nested
// metrics (`maintenanceSchedule.*`, `sensorValueOutOfRangeDetails.*`) fall
// back like unknown properties, and the network information is not indexed.
export const ASSET_LIST_SORT_PROPERTIES: Record<string, string> = {
    title: "title",
    serialNumber: "serialNumber",
    semiconductorTool: "semiconductorToolTitle",
    semiconductorChamber: "semiconductorChamberTitle",
    imei: "imei",
    product: "productName",
    productCode: "productCode",
    manufacturer: "manufacturerName",
    client: "clientName",
    // Sorts by country code, not by the translated name.
    country: "countryName",
    city: "city",
    location: "parentNodeTitle",
    iotConnection: "iotConnectionStatus",
    firmwareVersion: "firmwareVersion",
    modemFirmwareVersion: "modemFirmwareVersion",
    iotHubConnectivity: "iotHubConnectivityStatus",
    // Condition chips sort by their score: same order, finer grained.
    harshCondition: "harshConditionScore",
    harshConditionScore: "harshConditionScore",
    exhaustFilter: "exhaustFilterConditionScore",
    exhaustFilterConditionScore: "exhaustFilterConditionScore",
    missingSensor: "missingSensorValues",
    lastMissingSensorValuesAt: "lastMissingSensorValuesAt",
    sensorValueOutOfRange: "sensorValueOutOfRange",
    lastSensorValueOutOfRangeAt: "lastSensorValueOutOfRangeAt",
    vsdCandidate: "vsdCandidateScore",
    vsdCandidateScore: "vsdCandidateScore",
};

export const isAssetListColumnSortable = (field: string): boolean =>
    field in ASSET_LIST_SORT_PROPERTIES;

export const toAssetListSort = (sortModel: GridSortModel): Sort | undefined => {
    const item = sortModel[0];
    const property = item && item.sort ? ASSET_LIST_SORT_PROPERTIES[item.field] : undefined;
    if (!item || !property) return undefined;
    return {
        property,
        direction: item.sort === "desc" ? SortDirection.DESC : SortDirection.ASC,
    };
};

// URL representation: `sort=<column key>:<asc|desc>`.
export const parseAssetListSortParam = (value: unknown): GridSortModel => {
    if (typeof value !== "string") return [];
    const [field, direction] = value.split(":");
    if (!isAssetListColumnSortable(field)) return [];
    if (direction !== "asc" && direction !== "desc") return [];
    return [{ field, sort: direction }];
};

export const formatAssetListSortParam = (sortModel: GridSortModel): string | undefined => {
    const item = sortModel[0];
    if (!item || !item.sort || !isAssetListColumnSortable(item.field)) return undefined;
    return `${item.field}:${item.sort}`;
};
